/**
 * Targets the metrics page measures against.
 *
 * Hardcoded on purpose: per-user goals with editable deadlines need somewhere
 * to persist, and until the definitions are confirmed against real data a
 * config file makes a target change a reviewable diff.
 */

export type MetricKey = "utilization" | "deletionRate" | "manualRate";

export interface MetricTarget {
	key: MetricKey;
	label: string;
	/** Shown on hover. Name the denominator here. */
	help: string;
	target: number;
	direction: "higher" | "lower";
	/** ISO date, or null for "no date set yet". */
	deadline: string | null;
}

export const METRIC_TARGETS: Record<MetricKey, MetricTarget> = {
	utilization: {
		key: "utilization",
		label: "Utilization",
		help: "Hourglass exported hours as a share of the attorney's billed hours in the practice-management system. Source: /admin/billing/utilization.",
		target: 95,
		direction: "higher",
		deadline: null,
	},
	deletionRate: {
		key: "deletionRate",
		label: "Deleted",
		help: "Deleted entries as a share of everything Hourglass generated. Hand-created entries are excluded from the denominator — they were not generated by us, and counting them would dilute the rate.",
		// Placeholder until a real baseline lands; shown as provisional.
		target: 10,
		direction: "lower",
		deadline: null,
	},
	manualRate: {
		key: "manualRate",
		label: "Manual",
		help: "Hand-created entries as a share of all exported entries. High means attorneys are working around the capture rather than with it.",
		target: 10,
		direction: "lower",
		deadline: null,
	},
};

/** Targets not yet grounded in a measured baseline. */
export const PROVISIONAL_TARGETS: ReadonlySet<MetricKey> = new Set([
	"deletionRate",
	"manualRate",
]);

export type TargetStatus = "met" | "close" | "off" | "unknown";

/** How far a value is from its target, as a signed percentage-point gap. */
export function gapToTarget(
	value: number | null,
	target: MetricTarget,
): number | null {
	if (value === null) return null;
	return target.direction === "higher"
		? value - target.target
		: target.target - value;
}

/**
 * Status from the gap alone — NOT trajectory. With no history stored, any "on
 * track" claim would be invented. This is where a pace calculation would go.
 */
export function statusOf(
	value: number | null,
	target: MetricTarget,
): TargetStatus {
	const gap = gapToTarget(value, target);
	if (gap === null) return "unknown";
	if (gap >= 0) return "met";
	if (gap >= -10) return "close";
	return "off";
}

export const STATUS_LABEL: Record<TargetStatus, string> = {
	met: "Met",
	close: "Close",
	off: "Off target",
	unknown: "No data",
};

export const STATUS_CLASS: Record<TargetStatus, string> = {
	met: "text-green-700 dark:text-green-400",
	close: "text-amber-700 dark:text-amber-400",
	off: "text-red-700 dark:text-red-400",
	unknown: "text-muted-foreground",
};

// ─────────────────────────────────────────────
// Colour bands
// ─────────────────────────────────────────────

/** Per-column colour thresholds. Not derived from the targets above. */
export type Band = "green" | "orange" | "red";

interface BandRule {
	direction: "lower" | "higher";
	/** Boundaries are inclusive on the better side. */
	green: number;
	orange: number;
}

export const BAND_RULES: Record<string, BandRule> = {
	// Note: `tracked` and `exportedShare` are NOT here. They are banded
	// relationally against each other — see `comparisonBand`.
	utilization: { direction: "higher", green: 90, orange: 50 },
	deletedShare: { direction: "lower", green: 20, orange: 30 },
	manual: { direction: "lower", green: 20, orange: 40 },
};

/**
 * Band for the tracked/exported pair, from which of the two is larger.
 *
 * Both cells get the SAME colour: the question is not "is this number high"
 * but "is this person shipping more than they are sitting on". Exported ahead
 * is green, tracked ahead is red, a tie is orange. A fixed threshold could not
 * express that, because the same tracked percentage is healthy for someone who
 * exports the rest and unhealthy for someone who deletes it.
 */
export function comparisonBand(
	exportedPct: number | null,
	trackedPct: number | null,
): Band | null {
	if (exportedPct === null || trackedPct === null) return null;
	if (exportedPct > trackedPct) return "green";
	if (exportedPct < trackedPct) return "red";
	return "orange";
}

export function bandFor(columnKey: string, value: number | null): Band | null {
	const rule = BAND_RULES[columnKey];
	if (!rule || value === null) return null;
	if (rule.direction === "lower") {
		if (value <= rule.green) return "green";
		if (value <= rule.orange) return "orange";
		return "red";
	}
	if (value >= rule.green) return "green";
	if (value >= rule.orange) return "orange";
	return "red";
}

/** Tinted chips, not text colour — the table is scanned, not read. */
export const BAND_CLASS: Record<Band, string> = {
	green:
		"bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300",
	orange:
		"bg-orange-100 text-orange-800 dark:bg-orange-950/50 dark:text-orange-300",
	red: "bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300",
};

// ─────────────────────────────────────────────
// Excluded accounts
// ─────────────────────────────────────────────

/**
 * Never counted, never listed. Hourglass staff plus firm-side service accounts.
 *
 * `/admin/professionals` filters internal emails server-side, but only
 * `@hourglass.law` ones — the `@gouldratner.com` entries are firm service
 * accounts that would otherwise appear as real timekeepers.
 */
export const EXCLUDED_EMAILS: ReadonlySet<string> = new Set([
	"dayn@hourglass.law",
	"demo@hourglass.law",
	"tj@hourglass.law",
	"arturo@hourglass.law",
	"hourglass@gouldratner.com",
	"jwillis@gouldratner.com",
	"cneukirch@gouldratner.com",
]);

/**
 * True when this account should never appear on the page.
 *
 * Returns false for a missing email rather than guessing: dropping a real
 * timekeeper because their record lacks an address would silently shrink the
 * firm's numbers, which is worse than showing one account too many.
 */
export function isExcludedAccount(email?: string | null): boolean {
	if (!email) return false;
	return EXCLUDED_EMAILS.has(email.trim().toLowerCase());
}
